import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { XMarkIcon, MagnifyingGlassPlusIcon, MagnifyingGlassMinusIcon, ArrowsPointingOutIcon } from '@heroicons/react/24/outline'
import { useTablesWithColumnsQuery, type DatabaseTable, type DatabaseColumn, type DatabaseConstraint } from "@/hooks/useDatabase"

interface DatabaseDiagramModalProps {
  isOpen: boolean
  onClose: () => void
  connectionId: string
  database: string
  schema?: string
  databaseType?: string
}

interface TableNode {
  id: string
  name: string
  x: number
  y: number
  width: number
  height: number
  columns: {
    name: string
    type: string
    isPrimaryKey: boolean
    isForeignKey: boolean
    isNullable: boolean
  }[]
}

interface Relationship {
  id: string
  fromTable: string
  toTable: string
  fromColumn: string
  toColumn: string
  type?: 'one-to-one' | 'one-to-many' | 'many-to-many'
}

interface TableColumnData {
  tableName: string
  columns: DatabaseColumn[]
}

interface TableConstraintData {
  tableName: string
  constraints: DatabaseConstraint[]
}


export function DatabaseDiagramModal({ isOpen, onClose, connectionId, database, schema, databaseType }: DatabaseDiagramModalProps) {
  const [zoom, setZoom] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [selectedTable, setSelectedTable] = useState<string | null>(null)
  const [tableNodes, setTableNodes] = useState<TableNode[]>([])
  const [relationships, setRelationships] = useState<Relationship[]>([])
  const [draggingTable, setDraggingTable] = useState<string | null>(null)
  const [tableDragStart, setTableDragStart] = useState({ x: 0, y: 0, tableX: 0, tableY: 0 })
  const [hasDraggedTable, setHasDraggedTable] = useState(false)
  
  const svgRef = useRef<SVGSVGElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const lastDataRef = useRef<string>('')
  
  console.log('=== DATABASE DIAGRAM MODAL PARAMS ===');
  console.log('Connection ID:', connectionId);
  console.log('Database:', database);
  console.log('Schema:', schema);
  console.log('Database Type:', databaseType);
  console.log('Is Open:', isOpen);
  
  const { data: tablesWithData, isLoading, error } = useTablesWithColumnsQuery(connectionId, database, schema, { enabled: isOpen, databaseType })
  
  console.log('🎯 [ERD MODAL] Current query state:', {
    isOpen,
    isLoading,
    hasError: !!error,
    hasData: !!tablesWithData,
    connectionId,
    database,
    schema,
    databaseType
  });

  // Function to infer relationships from naming patterns when constraints aren't available
  const inferRelationshipsFromNaming = (tableNodes: TableNode[]): Relationship[] => {
    const inferredRelationships: Relationship[] = [];
    let relationshipId = 1000; // Start with high number to avoid conflicts
    
    console.log('Inferring relationships from naming patterns...');
    
    // Add some specific known relationships based on common patterns
    const knownRelationships = [
      // Post relationships
      { from: 'posts', to: 'users', fromCol: 'user_id', toCol: 'id' },
      { from: 'posts', to: 'categories', fromCol: 'category_id', toCol: 'id' },
      
      // Post tags many-to-many
      { from: 'post_tags', to: 'posts', fromCol: 'post_id', toCol: 'id' },
      { from: 'post_tags', to: 'tags', fromCol: 'tag_id', toCol: 'id' },
      
      // User relationships
      { from: 'user_profiles', to: 'users', fromCol: 'user_id', toCol: 'id' },
      { from: 'user_statistics', to: 'users', fromCol: 'user_id', toCol: 'id' },
      
      // Post details
      { from: 'post_details_with_category', to: 'posts', fromCol: 'post_id', toCol: 'id' },
      { from: 'post_details_with_category', to: 'categories', fromCol: 'category_id', toCol: 'id' },
    ];
    
    // Add known relationships if tables and columns exist
    for (const rel of knownRelationships) {
      const fromTable = tableNodes.find(t => t.name.toLowerCase() === rel.from.toLowerCase());
      const toTable = tableNodes.find(t => t.name.toLowerCase() === rel.to.toLowerCase());
      
      if (fromTable && toTable) {
        const fromColumn = fromTable.columns.find(c => c.name.toLowerCase() === rel.fromCol.toLowerCase());
        const toColumn = toTable.columns.find(c => c.name.toLowerCase() === rel.toCol.toLowerCase());
        
        if (fromColumn && toColumn) {
          const relationship: Relationship = {
            id: `known-${relationshipId++}`,
            fromTable: fromTable.name,
            toTable: toTable.name,
            fromColumn: fromColumn.name,
            toColumn: toColumn.name
          };
          
          inferredRelationships.push(relationship);
          console.log(`Added known relationship: ${relationship.fromTable}.${relationship.fromColumn} → ${relationship.toTable}.${relationship.toColumn}`);
        }
      }
    }
    
    for (const table of tableNodes) {
      for (const column of table.columns) {
        // Common foreign key patterns
        const patterns = [
          // Pattern: column_id -> column table
          { regex: /^(\w+)_id$/i, getTargetTable: (match: RegExpMatchArray) => `${match[1]}s` },
          { regex: /^(\w+)_id$/i, getTargetTable: (match: RegExpMatchArray) => match[1] },
          // Pattern: columnId -> column table  
          { regex: /^(\w+)Id$/i, getTargetTable: (match: RegExpMatchArray) => `${match[1].toLowerCase()}s` },
          { regex: /^(\w+)Id$/i, getTargetTable: (match: RegExpMatchArray) => match[1].toLowerCase() },
        ];
        
        for (const pattern of patterns) {
          const match = column.name.match(pattern.regex);
          if (match) {
            const targetTableName = pattern.getTargetTable(match);
            const targetTable = tableNodes.find(t => 
              t.name.toLowerCase() === targetTableName.toLowerCase()
            );
            
            if (targetTable) {
              // Look for id column in target table
              const targetColumn = targetTable.columns.find(col => 
                col.name.toLowerCase() === 'id' || 
                col.name.toLowerCase() === `${targetTable.name.toLowerCase()}_id` ||
                col.isPrimaryKey
              );
              
              if (targetColumn) {
                const relationship: Relationship = {
                  id: `inferred-${relationshipId++}`,
                  fromTable: table.name,
                  toTable: targetTable.name,
                  fromColumn: column.name,
                  toColumn: targetColumn.name
                };
                
                // Avoid duplicates
                const isDuplicate = inferredRelationships.some(rel => 
                  rel.fromTable === relationship.fromTable && 
                  rel.toTable === relationship.toTable &&
                  rel.fromColumn === relationship.fromColumn &&
                  rel.toColumn === relationship.toColumn
                );
                
                if (!isDuplicate) {
                  inferredRelationships.push(relationship);
                  console.log(`Inferred relationship: ${relationship.fromTable}.${relationship.fromColumn} → ${relationship.toTable}.${relationship.toColumn}`);
                }
              }
            }
          }
        }
      }
    }
    
    return inferredRelationships;
  }

  // Log raw data whenever it changes
  useEffect(() => {
    console.log('=== RAW TABLES WITH DATA ===');
    console.log('Full tablesWithData object:', tablesWithData);
    console.log('Table constraints:', tablesWithData.tableConstraints);
      
    for (const [index, tc] of tablesWithData.tableConstraints.entries()) {
      console.log(`Constraint data ${index + 1}:`, tc);
      for (const [cIndex, constraint] of tc.constraints.entries()) {
        console.log(`  Constraint ${cIndex + 1}:`, constraint);
      }
    }
  }, [tablesWithData])

  // Initialize table positions when tables data is loaded
  useEffect(() => {
    console.log('🚀 [ERD MODAL] useEffect triggered, tablesWithData:', !!tablesWithData);
    console.log('🚀 [ERD MODAL] tablesWithData object:', tablesWithData);
    console.log('✅ [ERD MODAL] tablesWithData exists, proceeding with processing');
    
    const {tables} = tablesWithData;
    const {tableColumns} = tablesWithData;
    const {tableConstraints} = tablesWithData;
    
    console.log('🔍 [ERD MODAL] Destructured data:');
    console.log('🔍 [ERD MODAL] - tables type:', typeof tables, 'is array:', Array.isArray(tables), 'length:', tables.length);
    console.log('🔍 [ERD MODAL] - tableColumns type:', typeof tableColumns, 'is array:', Array.isArray(tableColumns), 'length:', tableColumns.length);
    console.log('🔍 [ERD MODAL] - tableConstraints type:', typeof tableConstraints, 'is array:', Array.isArray(tableConstraints), 'length:', tableConstraints.length);
    console.log('🔍 [ERD MODAL] - actual tables data:', tables);
    console.log('🔍 [ERD MODAL] - actual tableColumns data:', tableColumns);
    
    console.log('=== DEBUGGING RELATIONSHIP DETECTION ===');
    console.log('Raw tablesWithData:', tablesWithData);
    console.log('Tables:', tables);
    console.log('Table columns:', tableColumns);
    console.log('Table constraints:', tableConstraints);
    
    // Detailed constraint debugging
    console.log('\n=== DETAILED CONSTRAINT DEBUGGING ===');
    for (const [index, tc] of tableConstraints.entries()) {
      console.log(`Table constraint ${index + 1}:`, tc);
      for (const [cIndex, constraint] of tc.constraints.entries()) {
        console.log(`  Constraint ${cIndex + 1}:`, constraint);
        console.log(`    Type: ${constraint.constraint_type}`);
        console.log(`    Name: ${constraint.constraint_name}`);
        console.log(`    Columns: ${JSON.stringify(constraint.columns)}`);
        console.log(`    Referenced Table: ${constraint.referenced_table}`);
      }
    }
    
    // Create a stable identifier for the data to prevent infinite re-renders
    const dataKey = JSON.stringify({
      tableCount: tables.length,
      tableNames: tables.map((t: DatabaseTable) => t.name).sort(),
      columnCounts: tableColumns.map((tc: TableColumnData) => ({ table: tc.tableName, count: tc.columns.length })).sort((a, b) => a.table.localeCompare(b.table)),
      constraintCounts: tableConstraints.map((tc: TableConstraintData) => ({ table: tc.tableName, count: tc.constraints.length })).sort((a, b) => a.table.localeCompare(b.table))
    });
    
    // Only proceed if data has actually changed
    if (lastDataRef.current === dataKey) {
      console.log('Data unchanged, skipping re-render');
      return;
    }
    lastDataRef.current = dataKey;
    
    if (tables.length === 0) {
      console.log('No tables found, resetting nodes and relationships');
      setTableNodes([]);
      setRelationships([]);
      return;
    }
    
    const tableNodes: TableNode[] = tables.map((table: DatabaseTable, index: number) => {
      const cols = Math.ceil(Math.sqrt(tables.length))
      const row = Math.floor(index / cols)
      const col = index % cols
      
      // Find columns for this table
      const tableColumnData = tableColumns.find((tc: TableColumnData) => tc.tableName === table.name);
      const columns = tableColumnData?.columns || [];
      
      console.log(`🏗️ [ERD MODAL] Processing table ${table.name}:`);
      console.log(`🏗️ [ERD MODAL] - tableColumnData found:`, !!tableColumnData);
      console.log(`🏗️ [ERD MODAL] - raw columns:`, columns);
      console.log(`🏗️ [ERD MODAL] - columns length:`, columns.length);
      
      if (columns.length > 0) {
        console.log(`🏗️ [ERD MODAL] - sample column structure:`, columns[0]);
        console.log(`🏗️ [ERD MODAL] - available keys in sample:`, Object.keys(columns[0]));
      }
      
      // Convert API column format to our internal format
      const processedColumns = columns.map((column: DatabaseColumn) => {
        // Handle multiple possible field name variations from different API responses
        const name = column.column_name || column.name || '';
        const type = column.data_type || column.type || 'UNKNOWN';
        
        // Check for primary key in all possible field variations
        const columnExt = column as unknown as Record<string, unknown>;
        const isPrimaryKey = !!(
          column.primary_key || 
          columnExt.is_primary_key ||
          columnExt.isPrimaryKey ||
          columnExt.is_primary ||
          false
        );
        
        // Check for nullable in all possible field variations  
        const isNullable = !!(
          column.nullable || 
          column.is_nullable ||
          columnExt.isNullable ||
          (column.is_nullable === false ? false : column.is_nullable) ||
          false
        );
        
        const processed = {
          name,
          type,
          isPrimaryKey,
          isForeignKey: false, // Will be determined from constraints
          isNullable
        };
        
        console.log(`🔄 [ERD MODAL] - processed column:`, { 
          original: column, 
          processed,
          keys: Object.keys(column),
          primaryKeyFields: {
            'column.primary_key': column.primary_key,
            'column.is_primary_key': columnExt.is_primary_key,
            'computed isPrimaryKey': isPrimaryKey
          }
        });
        return processed;
      });
      
      console.log(`✅ [ERD MODAL] Final processed columns for ${table.name}:`, processedColumns);
      
      // Find constraints for this table to identify foreign keys
      const tableConstraintData = tableConstraints.find((tc: TableConstraintData) => tc.tableName === table.name);
      const constraints = tableConstraintData?.constraints || [];
      
      // Mark foreign key columns - handle multiple constraint field name variations
      for (const constraint of constraints) {
        console.log(`🔍 [ERD MODAL] Processing constraint for ${table.name}:`, constraint);
        
        // Check for foreign key constraint with multiple possible field names
        const constraintType = constraint.constraint_type || constraint.type || '';
        const isForeignKey = constraintType === 'FOREIGN_KEY' || 
                           constraintType === 'FOREIGN KEY' ||
                           constraintType.toLowerCase() === 'foreign_key' ||
                           constraintType.toLowerCase().includes('foreign');
        
        // Get columns array with multiple possible field names - ensure it's always an array
        const constraintExt = constraint as unknown as Record<string, unknown>;
        let constraintColumns: string[] = [];
        
        if (constraint.columns && Array.isArray(constraint.columns)) {
          constraintColumns = constraint.columns;
        } else if (constraintExt.column_names && Array.isArray(constraintExt.column_names)) {
          constraintColumns = constraintExt.column_names as string[];
        } else if (constraint.column_name && typeof constraint.column_name === 'string') {
          constraintColumns = [constraint.column_name];
        }
        
        console.log(`🔍 [ERD MODAL] Constraint analysis:`, {
          constraintType,
          isForeignKey,
          constraintColumns,
          originalConstraint: constraint
        });
        
        if (isForeignKey && constraintColumns.length > 0) {
          for (const columnName of constraintColumns) {
            const column = processedColumns.find((col: { name: string; type: string; isPrimaryKey: boolean; isForeignKey: boolean; isNullable: boolean }) => col.name === columnName);
            if (column) {
              column.isForeignKey = true;
              console.log(`✅ [ERD MODAL] Marked column ${columnName} as foreign key in table ${table.name}`);
            } else {
              console.log(`⚠️ [ERD MODAL] Could not find column ${columnName} in processed columns for table ${table.name}`);
            }
          }
        }
      }
      
      const tableNode = {
        id: (table.name) || `table-${index}`,
        name: (table.name) || 'Unknown',
        x: col * 300 + 50,
        y: row * 250 + 50,
        width: 280,
        height: Math.max(120, 50 + (processedColumns.length * 24)), // Dynamic height based on column count
        columns: processedColumns
      };
      
      console.log(`🏢 [ERD MODAL] Final table node for ${table.name}:`, tableNode);
      console.log(`🏢 [ERD MODAL] - columns count: ${tableNode.columns.length}`);
      
      return tableNode;
    })
    
    // Build relationships from foreign key constraints
    const relationships: Relationship[] = []
    let relationshipId = 0
    
    console.log('=== BUILDING RELATIONSHIPS ===');
    console.log('Processing constraints from', tableConstraints.length, 'tables');
    
    for (const [index, tableConstraintData] of tableConstraints.entries()) {
      const {tableName} = tableConstraintData
      const {constraints} = tableConstraintData
      
      console.log(`\n--- Table ${index + 1}: ${tableName} ---`);
      console.log('Raw constraint data:', tableConstraintData);
      console.log('Constraints array:', constraints);
      console.log('Number of constraints:', constraints.length);
      
      if (!Array.isArray(constraints)) {
        console.warn(`Constraints for table ${tableName} is not an array:`, constraints);
        return;
      }
      
      for (const [constraintIndex, constraint] of constraints.entries()) {
        console.log(`\n  Constraint ${constraintIndex + 1}:`, constraint);
        console.log('  Constraint type:', constraint.constraint_type);
        console.log('  Constraint name:', constraint.constraint_name);
        console.log('  Referenced Table:', constraint.referenced_table);
        console.log('  Columns:', constraint.columns);
        
        // Extract referenced table and columns from API format
        console.log(`🔍 [CONSTRAINT DEBUG] Full constraint object:`, constraint);
        
        // Try multiple possible field names for referenced table/columns
        const referencedTable = constraint.referenced_table || 
                               constraint.references?.table ||
                               constraint.references?.referenced_table;
        const referencedColumns = constraint.referenced_columns || 
                                 constraint.references?.columns ||
                                 constraint.references?.referenced_columns;
        
        console.log('  Referenced table:', referencedTable);
        console.log('  Referenced columns:', referencedColumns);
        
        // Check for foreign key constraint with multiple possible field names - consistent with column processing
        const constraintType = constraint.constraint_type || constraint.type || '';
        const isForeignKey = constraintType === 'FOREIGN_KEY' || 
                           constraintType === 'FOREIGN KEY' ||
                           constraintType.toLowerCase() === 'foreign_key' ||
                           constraintType.toLowerCase().includes('foreign') ||
                           (referencedTable && referencedTable.length > 0);
        
        console.log('  Is Foreign Key?', isForeignKey);
        
        // Get columns with multiple possible field names - consistent with column processing
        const constraintExt = constraint as unknown as Record<string, unknown>;
        let constraintColumns: string[] = [];
        
        if (constraint.columns && Array.isArray(constraint.columns)) {
          constraintColumns = constraint.columns;
        } else if (constraintExt.column_names && Array.isArray(constraintExt.column_names)) {
          constraintColumns = constraintExt.column_names as string[];
        } else if (constraint.column_name && typeof constraint.column_name === 'string') {
          constraintColumns = [constraint.column_name];
        }
        
        if (isForeignKey) {
          console.log('  ✓ Found foreign key constraint!');
          
          if (referencedTable && 
              constraintColumns && 
              referencedColumns &&
              constraintColumns.length > 0 &&
              referencedColumns.length > 0) {
            
            // Create relationship for each column pair
            const minLength = Math.min(constraintColumns.length, referencedColumns.length)
            console.log(`  Creating ${minLength} relationships from column pairs`);
            
            for (let i = 0; i < minLength; i++) {
              const relationship = {
                id: `rel-${relationshipId++}`,
                fromTable: tableName,
                toTable: referencedTable,
                fromColumn: constraintColumns[i],
                toColumn: referencedColumns[i]
              }
              
              relationships.push(relationship)
              console.log('  ✓ Added relationship:', relationship);
            }
          } else {
            console.log('  ✗ Incomplete constraint data:');
            console.log('    - referencedTable:', referencedTable);
            console.log('    - columns:', constraintColumns);
            console.log('    - referenced_columns:', referencedColumns);
            
            // Try to extract foreign key info from constraint definition or name
            if (constraint.definition) {
              console.log('    - definition:', constraint.definition);
              // Try to parse foreign key from definition string
              const fkMatch = /REFERENCES\s+(\w+)\s*\(([^)]+)\)/i.exec(constraint.definition);
              if (fkMatch) {
                const [, refTable, refColumn] = fkMatch;
                const trimmedRefColumn = refColumn.trim();
                console.log('    - Parsed from definition: table =', refTable, ', column =', trimmedRefColumn);
                
                // Try to infer the source column from constraint name or table structure
                if (constraintColumns && constraintColumns.length > 0) {
                  const relationship = {
                    id: `rel-${relationshipId++}`,
                    fromTable: tableName,
                    toTable: refTable,
                    fromColumn: constraintColumns[0],
                    toColumn: trimmedRefColumn
                  }
                  
                  relationships.push(relationship)
                  console.log('  ✓ Added relationship from definition:', relationship);
                }
              }
            }
            
            // Additional fallback: try to parse from constraint name pattern
            if (constraint.constraint_name && constraint.constraint_name.toLowerCase().includes('fk_')) {
              console.log('    - Attempting to parse from constraint name:', constraint.constraint_name);
              // Pattern: fk_posts_category -> posts references category
              const nameMatch = /fk_(\w+)_(\w+)/i.exec(constraint.constraint_name);
              if (nameMatch && constraint.columns && constraint.columns.length > 0) {
                const [, sourceTable, targetTable] = nameMatch;
                
                // Check if the constraint belongs to the correct table
                if (sourceTable.toLowerCase() === tableName.toLowerCase()) {
                  const relationship = {
                    id: `rel-${relationshipId++}`,
                    fromTable: tableName,
                    toTable: targetTable,
                    fromColumn: constraint.columns[0],
                    toColumn: 'id' // Common assumption for referenced column
                  }
                  
                  relationships.push(relationship)
                  console.log('  ✓ Added relationship from constraint name:', relationship);
                }
              }
            }
          }
        } else {
          console.log(`  - Skipping non-FK constraint (type: ${constraint.constraint_type})`);
        }
      }
    }
    
    console.log('\n=== RELATIONSHIP SUMMARY ===');
    console.log('Total relationships found from constraints:', relationships.length);
    for (const [index, rel] of relationships.entries()) {
      console.log(`${index + 1}. ${rel.fromTable}.${rel.fromColumn} → ${rel.toTable}.${rel.toColumn}`);
    }
    
    // Debug: Check if we have the expected tables from your API example
    const tableNames = tableNodes.map(t => t.name);
    console.log('\n=== TABLE AVAILABILITY CHECK ===');
    console.log('Available tables:', tableNames);
    console.log('Looking for posts table:', tableNames.includes('posts'));
    console.log('Looking for categories table:', tableNames.includes('categories'));
    console.log('Looking for users table:', tableNames.includes('users'));
    
    // Check if expected relationships were created
    const postsToCategories = relationships.find(r => 
      r.fromTable === 'posts' && r.toTable === 'categories' && r.fromColumn === 'category_id'
    );
    const postsToUsers = relationships.find(r => 
      r.fromTable === 'posts' && r.toTable === 'users' && r.fromColumn === 'user_id'
    );
    
    console.log('Posts → Categories relationship found:', !!postsToCategories);
    console.log('Posts → Users relationship found:', !!postsToUsers);
    
    if (postsToCategories) console.log('Posts → Categories details:', postsToCategories);
    if (postsToUsers) console.log('Posts → Users details:', postsToUsers);
    
    // If no relationships found from constraints, try to infer them from table/column naming patterns
    if (relationships.length === 0) {
      console.log('\n=== INFERRING RELATIONSHIPS FROM NAMING PATTERNS ===');
      const inferredRelationships = inferRelationshipsFromNaming(tableNodes);
      relationships.push(...inferredRelationships);
      console.log('Inferred relationships:', inferredRelationships.length);
      for (const [index, rel] of inferredRelationships.entries()) {
        console.log(`${index + 1}. ${rel.fromTable}.${rel.fromColumn} → ${rel.toTable}.${rel.toColumn} (inferred)`);
      }
    }
    
    setTableNodes(tableNodes);
    setRelationships(relationships);
    
    // Debug logging
    console.log('Tables processed:', tables.length);
    console.log('Final relationships count:', relationships.length);
    console.log('All relationships:', relationships);
    if (relationships.length > 0) {
      console.log('Sample relationship:', relationships[0]);
    }
  }, [tablesWithData])

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.target === svgRef.current) {
      setIsDragging(true)
      setDragStart({
        x: e.clientX - pan.x,
        y: e.clientY - pan.y
      })
    }
  }, [pan.x, pan.y])

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      })
    } else if (draggingTable) {
      // Calculate new position for the dragging table
      const deltaX = (e.clientX - tableDragStart.x) / zoom
      const deltaY = (e.clientY - tableDragStart.y) / zoom
      
      // Track if we've actually moved the table significantly
      if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) {
        setHasDraggedTable(true)
      }
      
      setTableNodes(prev => prev.map(table => 
        table.id === draggingTable 
          ? { ...table, x: tableDragStart.tableX + deltaX, y: tableDragStart.tableY + deltaY }
          : table
      ))
    }
  }, [isDragging, dragStart.x, dragStart.y, draggingTable, tableDragStart.x, tableDragStart.y, tableDragStart.tableX, tableDragStart.tableY, zoom])

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
    setDraggingTable(null)
    // Reset drag tracking after a short delay to allow click events to process
    setTimeout(() => setHasDraggedTable(false), 10)
  }, [])

  const handleTableMouseDown = useCallback((e: React.MouseEvent, tableId: string) => {
    e.stopPropagation() // Prevent canvas panning
    const table = tableNodes.find(t => t.id === tableId)
    if (!table) return
    
    setHasDraggedTable(false)
    setDraggingTable(tableId)
    setTableDragStart({
      x: e.clientX,
      y: e.clientY,
      tableX: table.x,
      tableY: table.y
    })
  }, [tableNodes])

  const handleTableClick = useCallback((tableId: string) => {
    // Only select/deselect if we haven't dragged
    if (!hasDraggedTable) {
      const isSelected = selectedTable === tableId
      setSelectedTable(isSelected ? null : tableId)
    }
  }, [hasDraggedTable, selectedTable])

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev * 1.2, 3))
  }

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev / 1.2, 0.3))
  }

  const handleFitToScreen = () => {
    if (tableNodes.length === 0) return
    
    const container = containerRef.current
    if (!container) return
    
    const minX = Math.min(...tableNodes.map(t => t.x))
    const maxX = Math.max(...tableNodes.map(t => t.x + t.width))
    const minY = Math.min(...tableNodes.map(t => t.y))
    const maxY = Math.max(...tableNodes.map(t => t.y + t.height))
    
    const contentWidth = maxX - minX + 100
    const contentHeight = maxY - minY + 100
    const containerWidth = container.offsetWidth
    const containerHeight = container.offsetHeight
    
    const scaleX = containerWidth / contentWidth
    const scaleY = containerHeight / contentHeight
    const scale = Math.min(scaleX, scaleY, 1)
    
    setZoom(scale)
    setPan({
      x: (containerWidth - contentWidth * scale) / 2 - minX * scale,
      y: (containerHeight - contentHeight * scale) / 2 - minY * scale
    })
  }

  // Create stable event handlers for tables
  const tableEventHandlers = useMemo(() => {
    const handlers = new Map<string, {
      onClick: () => void
      onMouseDown: (e: React.MouseEvent) => void
    }>()
    
    for (const table of tableNodes) {
      handlers.set(table.id, {
        onClick: () => handleTableClick(table.id),
        onMouseDown: (e: React.MouseEvent) => handleTableMouseDown(e, table.id)
      })
    }
    
    return handlers
  }, [tableNodes, handleTableClick, handleTableMouseDown])

  const renderTable = useCallback((table: TableNode) => {
    const isSelected = selectedTable === table.id
    const isDraggingThis = draggingTable === table.id
    const handlers = tableEventHandlers.get(table.id)
    
    if (!handlers) return null
    
    return (
      <g 
        key={table.id} 
        transform={`translate(${table.x}, ${table.y})`}
        style={{
          transition: isDraggingThis ? 'none' : 'transform 0.2s ease-out'
        }}
      >
        {/* Table container */}
        <rect
          width={table.width}
          height={table.height}
          fill="white"
          stroke={isSelected ? "#3b82f6" : isDraggingThis ? "#10b981" : "#d1d5db"}
          strokeWidth={isSelected || isDraggingThis ? 2 : 1}
          rx={8}
          className={`cursor-pointer hover:stroke-blue-400 hover:stroke-2 transition-all duration-200 ${
            isDraggingThis ? 'cursor-grabbing' : 'cursor-grab'
          }`}
          onClick={handlers.onClick}
          onMouseDown={handlers.onMouseDown}
          style={{
            filter: isDraggingThis ? 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2))' : undefined,
            opacity: isDraggingThis ? 0.9 : 1
          }}
        />
        
        {/* Table header */}
        <rect
          width={table.width}
          height={32}
          fill="#f3f4f6"
          stroke="#d1d5db"
          strokeWidth={1}
          rx={8}
          className={`cursor-pointer ${
            isDraggingThis ? 'cursor-grabbing' : 'cursor-grab'
          }`}
          onMouseDown={handlers.onMouseDown}
        />
        <rect
          width={table.width}
          height={24}
          fill="#f3f4f6"
          rx={8}
          className={`cursor-pointer ${
            isDraggingThis ? 'cursor-grabbing' : 'cursor-grab'
          }`}
          onMouseDown={handlers.onMouseDown}
        />
        
        {/* Table name */}
        <text
          x={table.width / 2}
          y={20}
          textAnchor="middle"
          className={`fill-gray-900 font-semibold text-sm cursor-pointer ${
            isDraggingThis ? 'cursor-grabbing' : 'cursor-grab'
          }`}
          onMouseDown={handlers.onMouseDown}
          style={{ userSelect: 'none' }}
        >
          {table.name}
        </text>
        
        {/* Columns */}
        {table.columns.slice(0, 8).map((column, index) => (
          <g key={column.name} transform={`translate(0, ${40 + index * 24})`}>
            {/* Column background for better readability */}
            <rect
              width={table.width}
              height={22}
              fill={column.isPrimaryKey ? "#fef3c7" : column.isForeignKey ? "#dbeafe" : "transparent"}
              opacity={0.3}
            />
            
            <text
              x={8}
              y={16}
              className={`text-xs ${column.isPrimaryKey ? 'fill-amber-700 font-semibold' : column.isForeignKey ? 'fill-blue-700 font-medium' : 'fill-gray-700'}`}
            >
              {column.isPrimaryKey && '🔑 '}
              {column.isForeignKey && '🔗 '}
              {column.name}
              {column.isNullable ? '' : ' *'}
            </text>
            <text
              x={table.width - 8}
              y={16}
              textAnchor="end"
              className="fill-gray-500 text-xs"
            >
              {column.type}
            </text>
          </g>
        ))}
        
        {table.columns.length > 8 && (
          <text
            x={table.width / 2}
            y={40 + 8 * 24 + 16}
            textAnchor="middle"
            className="fill-gray-400 text-xs italic"
          >
            ... and {table.columns.length - 8} more columns
          </text>
        )}
      </g>
    )
  }, [selectedTable, draggingTable, tableEventHandlers])

  const renderRelationship = (rel: Relationship) => {
    console.log(`\n=== RENDERING RELATIONSHIP ===`);
    console.log('Relationship:', rel);
    console.log('Available table nodes:', tableNodes.map(t => t.name));
    
    const fromTable = tableNodes.find(t => t.name === rel.fromTable)
    const toTable = tableNodes.find(t => t.name === rel.toTable)
    
    console.log('From table found:', !!fromTable, fromTable?.name);
    console.log('To table found:', !!toTable, toTable?.name);
    
    if (!fromTable || !toTable) {
      console.log('❌ Relationship not rendered - missing table(s)');
      return null;
    }
    
    // Skip self-referential relationships for cleaner visualization
    if (fromTable.id === toTable.id) {
      console.log('❌ Relationship not rendered - self-referential');
      return null;
    }
    
    console.log('✅ Rendering relationship arrow');
    
    // Find the column positions for visual reference
    const fromColumnIndex = fromTable.columns.findIndex(col => col.name === rel.fromColumn)
    const toColumnIndex = toTable.columns.findIndex(col => col.name === rel.toColumn)
    
    // Calculate connection points with consistent spacing from table edges
    const EDGE_SPACING = 12 // Increased spacing from table edges for better visibility
    
    // Get table bounds
    const fromBounds = {
      left: fromTable.x,
      right: fromTable.x + fromTable.width,
      top: fromTable.y,
      bottom: fromTable.y + fromTable.height,
      centerX: fromTable.x + fromTable.width / 2,
      centerY: fromTable.y + fromTable.height / 2
    }
    
    const toBounds = {
      left: toTable.x,
      right: toTable.x + toTable.width,
      top: toTable.y,
      bottom: toTable.y + toTable.height,
      centerX: toTable.x + toTable.width / 2,
      centerY: toTable.y + toTable.height / 2
    }
    
    // Calculate preferred connection points based on column positions
    const fromColumnY = fromColumnIndex >= 0 ? fromTable.y + 40 + fromColumnIndex * 24 + 12 : fromBounds.centerY
    const toColumnY = toColumnIndex >= 0 ? toTable.y + 40 + toColumnIndex * 24 + 12 : toBounds.centerY
    
    // Determine the best connection strategy based on table positions
    const deltaX = toBounds.centerX - fromBounds.centerX
    const deltaY = toBounds.centerY - fromBounds.centerY
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)
    
    let fromX, fromY, toX, toY, fromDirection, toDirection
    
    // Improved connection logic - avoid going behind tables
    const tablesOverlapHorizontally = !(fromBounds.right < toBounds.left || toBounds.right < fromBounds.left)
    const tablesOverlapVertically = !(fromBounds.bottom < toBounds.top || toBounds.bottom < fromBounds.top)
    
    // Choose connection strategy based on table layout
    if (!tablesOverlapHorizontally && Math.abs(deltaX) > Math.abs(deltaY)) {
      // Tables are clearly separated horizontally - connect horizontally
      if (deltaX > 0) {
        // From table right edge to target table left edge
        fromX = fromBounds.right + EDGE_SPACING
        fromY = Math.max(fromBounds.top + 35, Math.min(fromBounds.bottom - 15, fromColumnY))
        toX = toBounds.left - EDGE_SPACING
        toY = Math.max(toBounds.top + 35, Math.min(toBounds.bottom - 15, toColumnY))
        fromDirection = 'right'
        toDirection = 'left'
      } else {
        // From table left edge to target table right edge
        fromX = fromBounds.left - EDGE_SPACING
        fromY = Math.max(fromBounds.top + 35, Math.min(fromBounds.bottom - 15, fromColumnY))
        toX = toBounds.right + EDGE_SPACING
        toY = Math.max(toBounds.top + 35, Math.min(toBounds.bottom - 15, toColumnY))
        fromDirection = 'left'
        toDirection = 'right'
      }
    } else if (!tablesOverlapVertically && Math.abs(deltaY) > 60) {
      // Tables are clearly separated vertically - connect vertically
      if (deltaY > 0) {
        // From table bottom to target table top
        fromX = Math.max(fromBounds.left + 25, Math.min(fromBounds.right - 25, fromBounds.centerX))
        fromY = fromBounds.bottom + EDGE_SPACING
        toX = Math.max(toBounds.left + 25, Math.min(toBounds.right - 25, toBounds.centerX))
        toY = toBounds.top - EDGE_SPACING
        fromDirection = 'bottom'
        toDirection = 'top'
      } else {
        // From table top to target table bottom
        fromX = Math.max(fromBounds.left + 25, Math.min(fromBounds.right - 25, fromBounds.centerX))
        fromY = fromBounds.top - EDGE_SPACING
        toX = Math.max(toBounds.left + 25, Math.min(toBounds.right - 25, toBounds.centerX))
        toY = toBounds.bottom + EDGE_SPACING
        fromDirection = 'top'
        toDirection = 'bottom'
      }
    } else {
      // Tables are overlapping or close - use safest connection strategy with more spacing
      const SAFE_SPACING = EDGE_SPACING * 3 // Use more spacing for overlapping tables
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        // Connect horizontally with larger spacing to avoid overlap
        if (deltaX > 0) {
            fromX = fromBounds.right + SAFE_SPACING
            fromY = Math.max(fromBounds.top + 35, Math.min(fromBounds.bottom - 15, fromColumnY))
            toX = toBounds.left - SAFE_SPACING
            toY = Math.max(toBounds.top + 35, Math.min(toBounds.bottom - 15, toColumnY))
            fromDirection = 'right'
            toDirection = 'left'
          } else {
            fromX = fromBounds.left - SAFE_SPACING
            fromY = Math.max(fromBounds.top + 35, Math.min(fromBounds.bottom - 15, fromColumnY))
            toX = toBounds.right + SAFE_SPACING
            toY = Math.max(toBounds.top + 35, Math.min(toBounds.bottom - 15, toColumnY))
            fromDirection = 'left'
            toDirection = 'right'
          }
        } else {
          // Connect vertically with larger spacing
          if (deltaY > 0) {
            fromX = Math.max(fromBounds.left + 25, Math.min(fromBounds.right - 25, fromBounds.centerX))
            fromY = fromBounds.bottom + SAFE_SPACING
            toX = Math.max(toBounds.left + 25, Math.min(toBounds.right - 25, toBounds.centerX))
            toY = toBounds.top - SAFE_SPACING
            fromDirection = 'bottom'
            toDirection = 'top'
          } else {
            fromX = Math.max(fromBounds.left + 25, Math.min(fromBounds.right - 25, fromBounds.centerX))
            fromY = fromBounds.top - SAFE_SPACING
            toX = Math.max(toBounds.left + 25, Math.min(toBounds.right - 25, toBounds.centerX))
            toY = toBounds.bottom + SAFE_SPACING
            fromDirection = 'top'
            toDirection = 'bottom'
          }
        }
      }
    
    // Calculate control points for smooth curves that avoid tables
    const controlDistance = Math.min(Math.max(distance * 0.4, 80), 200)
    let cp1X = fromX, cp1Y = fromY, cp2X = toX, cp2Y = toY
    
    // Generate control points based on connection direction
    switch (fromDirection) {
      case 'right':
        cp1X = fromX + controlDistance
        cp1Y = fromY
        break
      case 'left':
        cp1X = fromX - controlDistance
        cp1Y = fromY
        break
      case 'bottom':
        cp1X = fromX
        cp1Y = fromY + controlDistance
        break
      case 'top':
        cp1X = fromX
        cp1Y = fromY - controlDistance
        break
    }
    
    switch (toDirection) {
      case 'left':
        cp2X = toX - controlDistance
        cp2Y = toY
        break
      case 'right':
        cp2X = toX + controlDistance
        cp2Y = toY
        break
      case 'top':
        cp2X = toX
        cp2Y = toY - controlDistance
        break
      case 'bottom':
        cp2X = toX
        cp2Y = toY + controlDistance
        break
    }
    
    // Create smooth cubic bezier curve
    const pathD = `M ${fromX} ${fromY} C ${cp1X} ${cp1Y}, ${cp2X} ${cp2Y}, ${toX} ${toY}`
    
    // Calculate label position at curve midpoint (t=0.5 for bezier curve)
    const labelX = 0.125 * fromX + 0.375 * cp1X + 0.375 * cp2X + 0.125 * toX
    const labelY = 0.125 * fromY + 0.375 * cp1Y + 0.375 * cp2Y + 0.125 * toY
    
    // Determine arrow marker and color based on relationship type
    const markerEnd = rel.type === 'one-to-one' ? 'url(#arrowhead-one-to-one)' :
                      rel.type === 'many-to-many' ? 'url(#arrowhead-many-to-many)' :
                      'url(#arrowhead)'
    
    const relationshipColor = rel.type === 'one-to-one' ? '#059669' :
                             rel.type === 'many-to-many' ? '#dc2626' :
                             '#4f46e5'
    
    return (
      <g key={rel.id} className="relationship-line">
        {/* Subtle background line for better visibility */}
        <path
          d={pathD}
          stroke="rgba(255, 255, 255, 0.9)"
          strokeWidth={5}
          fill="none"
          opacity={0.7}
          style={{ 
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
            pointerEvents: 'none'
          }}
        />
        
        {/* Main relationship line with arrow */}
        <path
          d={pathD}
          stroke={relationshipColor}
          strokeWidth={2.5}
          fill="none"
          markerEnd={markerEnd}
          opacity={0.9}
          className="hover:opacity-1 hover:stroke-width-3 transition-all duration-200"
          style={{ 
            filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15))',
            cursor: 'pointer',
            strokeLinecap: 'round',
            strokeLinejoin: 'round'
          }}
        />
        
        {/* Relationship label with background */}
        <g className="relationship-label">
          <rect
            x={labelX - 16}
            y={labelY - 9}
            width={32}
            height={18}
            fill="white"
            stroke={relationshipColor}
            strokeWidth={1.5}
            rx={9}
            opacity={0.98}
            className="hover:opacity-1"
            style={{ 
              filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.12))'
            }}
          />
          <text
            x={labelX}
            y={labelY + 4}
            textAnchor="middle"
            className="fill-gray-700 text-xs font-bold"
            style={{ 
              pointerEvents: 'none',
              userSelect: 'none'
            }}
          >
            FK
          </text>
        </g>
        
        {/* Tooltip on hover */}
        <title>
          Foreign Key: {rel.fromTable}.{rel.fromColumn} → {rel.toTable}.{rel.toColumn}
          {rel.type && ` (${rel.type.replace('-', ' to ')})`}
        </title>
      </g>
    )
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full h-full max-w-7xl max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Database Diagram - {schema ? `${database}.${schema}` : database}
          </h2>
          
          {/* Controls */}
          <div className="flex items-center space-x-2">
            <button type="button"
              onClick={handleZoomOut}
              className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
              title="Zoom Out"
            >
              <MagnifyingGlassMinusIcon className="w-5 h-5" />
            </button>
            
            <span className="text-sm text-gray-600 dark:text-gray-400 min-w-[60px] text-center">
              {Math.round(zoom * 100)}%
            </span>
            
            <button type="button"
              onClick={handleZoomIn}
              className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
              title="Zoom In"
            >
              <MagnifyingGlassPlusIcon className="w-5 h-5" />
            </button>
            
            <button type="button"
              onClick={handleFitToScreen}
              className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
              title="Fit to Screen"
            >
              <ArrowsPointingOutIcon className="w-5 h-5" />
            </button>
            
            <button type="button"
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Diagram Content */}
        <div 
          ref={containerRef}
          className="flex-1 overflow-hidden bg-gray-50 dark:bg-gray-900 relative"
          style={{ height: 'calc(100% - 73px)' }}
        >
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-gray-400">Loading tables, columns, and relationships...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="text-red-500 mb-4">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.232 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Error Loading Tables</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Unable to load tables for {schema ? `schema "${schema}"` : `database "${database}"`}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-500">
                  Error: {error instanceof Error ? error.message : 'Unknown error'}
                </p>
              </div>
            </div>
          ) : tableNodes.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="text-gray-400 mb-4">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-2.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 009.586 13H7" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Tables Found</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  No tables found in {schema ? `schema "${schema}"` : `database "${database}"`}
                </p>
              </div>
            </div>
          ) : (
            <svg
              ref={svgRef}
              width="100%"
              height="100%"
              className={draggingTable 
                  ? 'cursor-grabbing' 
                  : isDragging 
                    ? 'cursor-grabbing' 
                    : 'cursor-grab'}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              {/* Arrow markers definition */}
              <defs>
                {/* Foreign Key Arrow (One-to-Many) - Standard relationship */}
                <marker
                  id="arrowhead"
                  markerWidth="16"
                  markerHeight="14"
                  refX="15"
                  refY="7"
                  orient="auto"
                  markerUnits="strokeWidth"
                  viewBox="0 0 16 14"
                >
                  <path
                    d="M 0 0 L 16 7 L 0 14 L 4 7 Z"
                    fill="#4f46e5"
                    stroke="#4f46e5"
                    strokeWidth="0.5"
                    strokeLinejoin="round"
                  />
                </marker>
                
                {/* One-to-One Arrow - More distinctive design */}
                <marker
                  id="arrowhead-one-to-one"
                  markerWidth="20"
                  markerHeight="14"
                  refX="19"
                  refY="7"
                  orient="auto"
                  markerUnits="strokeWidth"
                  viewBox="0 0 20 14"
                >
                  <g>
                    {/* Vertical line indicating "one" */}
                    <line 
                      x1="2" 
                      y1="2" 
                      x2="2" 
                      y2="12" 
                      stroke="#059669" 
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    {/* Arrow head */}
                    <path
                      d="M 4 0 L 20 7 L 4 14 L 8 7 Z"
                      fill="#059669"
                      stroke="#059669"
                      strokeWidth="0.5"
                      strokeLinejoin="round"
                    />
                  </g>
                </marker>
                
                {/* Many-to-Many Arrow - Double arrow design */}
                <marker
                  id="arrowhead-many-to-many"
                  markerWidth="24"
                  markerHeight="14"
                  refX="23"
                  refY="7"
                  orient="auto"
                  markerUnits="strokeWidth"
                  viewBox="0 0 24 14"
                >
                  <g>
                    {/* First arrow */}
                    <path
                      d="M 0 2 L 12 7 L 0 12 L 3 7 Z"
                      fill="none"
                      stroke="#dc2626"
                      strokeWidth="1.5"
                      strokeLinejoin="round"
                    />
                    {/* Second arrow */}
                    <path
                      d="M 8 0 L 24 7 L 8 14 L 12 7 Z"
                      fill="#dc2626"
                      stroke="#dc2626"
                      strokeWidth="0.5"
                      strokeLinejoin="round"
                    />
                  </g>
                </marker>
              </defs>
              
              <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
                {/* Render tables first */}
                {tableNodes.length > 0 && (
                  <g className="tables-layer">
                    {tableNodes.map((table) => {
                      try {
                        return renderTable(table)
                      } catch (error) {
                        console.error(`Error rendering table ${table.id}:`, error)
                        return null
                      }
                    })}
                  </g>
                )}
                
                {/* Render relationships on top of tables */}
                {relationships.length > 0 && (
                  <g className="relationships-layer">
                    {relationships.map((rel) => {
                      try {
                        return renderRelationship(rel)
                      } catch (error) {
                        console.error(`Error rendering relationship ${rel.id}:`, error)
                        return null
                      }
                    })}
                  </g>
                )}
              </g>
            </svg>
          )}
          
          {/* Only show legend and instructions when we have tables */}
          {!isLoading && !error && tableNodes.length > 0 && (
            <>
              {/* Legend */}
              <div className="absolute bottom-4 left-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-3 border border-gray-200 dark:border-gray-700">
                <h4 className="font-semibold text-sm text-gray-900 dark:text-white mb-2">Legend</h4>
                <div className="space-y-1 text-xs">
                  <div className="flex items-center">
                    <span className="mr-2">🔑</span>
                    <span className="text-gray-700 dark:text-gray-300">Primary Key</span>
                  </div>
                  <div className="flex items-center">
                    <span className="mr-2">🔗</span>
                    <span className="text-gray-700 dark:text-gray-300">Foreign Key</span>
                  </div>
                  <div className="flex items-center">
                    <span className="mr-2">*</span>
                    <span className="text-gray-700 dark:text-gray-300">Required (NOT NULL)</span>
                  </div>
                  <div className="flex items-center">
                    <svg width="20" height="8" className="mr-2">
                      <defs>
                        <marker
                          id="legend-arrow"
                          markerWidth="10"
                          markerHeight="8"
                          refX="9"
                          refY="4"
                          orient="auto"
                        >
                          <polygon
                            points="0 0, 10 4, 0 8"
                            fill="#4f46e5"
                          />
                        </marker>
                      </defs>
                      <path d="M2 4 L16 4" stroke="#4f46e5" strokeWidth="2" markerEnd="url(#legend-arrow)"/>
                    </svg>
                    <span className="text-gray-700 dark:text-gray-300">Foreign Key Relationship</span>
                  </div>
                  {relationships.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                      <div className="text-gray-600 dark:text-gray-400 font-medium">
                        {relationships.length} relationship{relationships.length !== 1 ? 's' : ''} found
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Instructions and debug info */}
              <div className="absolute top-4 right-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-3 border border-gray-200 dark:border-gray-700 max-w-xs">
                <h4 className="font-semibold text-sm text-gray-900 dark:text-white mb-2">Controls</h4>
                <div className="space-y-1 text-xs text-gray-700 dark:text-gray-300">
                  <div>• Click and drag canvas to pan</div>
                  <div>• Click and drag tables to move</div>
                  <div>• Use zoom controls to scale</div>
                  <div>• Click tables to select</div>
                </div>
                
                {/* Debug information */}
                <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-600">
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    <div>Tables: {tableNodes.length}</div>
                    <div>Relationships: {relationships.length}</div>
                    <div>Column Data: {tablesWithData.tableColumns.length || 0}</div>
                    <div>Raw constraint tables: {tablesWithData.tableConstraints.length}</div>
                    <div className="mt-1">
                      {tablesWithData.tableColumns.map((tc: unknown, i: number) => (
                        <div key={i} className="text-xs">
                          {(() => {
                            const name = (tc && typeof tc === 'object' && 'tableName' in (tc as { tableName?: unknown }) && typeof (tc as { tableName?: unknown }).tableName === 'string')
                              ? (tc as { tableName?: string }).tableName
                              : ''
                            const cols = (tc && typeof tc === 'object' && 'columns' in (tc as { columns?: unknown[] }) && Array.isArray((tc as { columns?: unknown[] }).columns))
                              ? (((tc as { columns?: unknown[] }).columns || []).length)
                              : 0
                            return `${name}: ${cols} columns`
                          })()}
                        </div>
                      ))}
                    </div>
                    <div className="mt-1">
                      {tablesWithData.tableConstraints.map((tc: TableConstraintData, i: number) => (
                        <div key={i} className="text-xs">
                          {tc.tableName}: {tc.constraints.length} constraints
                        </div>
                      ))}
                    </div>
                    {relationships.length === 0 && tableNodes.length > 0 && (
                      <div className="text-amber-600 dark:text-amber-400 mt-1">
                        No foreign key relationships detected
                      </div>
                    )}
                    {tablesWithData.tableConstraints.length > 0 && (
                      <div className="text-blue-600 dark:text-blue-400 mt-1">
                        Raw constraints available - check console
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
